import React from 'react';
import { NextPage, GetStaticProps, GetStaticPaths } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { cx } from '@emotion/css';
import * as models from 'src/models';
import * as configs from 'src/configs';
import * as services from 'src/services';
import * as styles from 'src/styles';
import * as array from 'src/lib/array';
import * as record from 'src/lib/record';
import * as cssClass from 'src/lib/cssClass';
import { LocaleConfigProvider } from 'src/contexts/LocaleConfig';
import { BuildTimestampProvider } from 'src/contexts/BuildTimestamp';
import useTheme from 'src/hooks/useTheme';
import AppNavbar from 'src/components/AppNavbar';
import AppHeader from 'src/components/AppHeader';
import AppFooter from 'src/components/AppFooter';
import Divider from 'src/components/Divider';
import Body from 'src/components/Body';
import Unit from 'src/components/Unit';
import InstitutionDashboard from 'src/views/InstitutionDashboard';

export type Props = {
  timestamp: number;
  localeConfig: configs.locale.types.Config;
  institution: models.institution.types.Institution;
  institutionSeed: models.institutionSeed.types.InstitutionSeed | null;
};

const Root: NextPage<Props> = ({ timestamp, institution, institutionSeed, localeConfig }) => {
  const theme = useTheme();

  return (
    <BuildTimestampProvider value={timestamp}>
      <LocaleConfigProvider value={localeConfig}>
        <AppNavbar className={cx(cssClass.mb('20px'))}>
          <AppHeader className={styles.views.main(theme)} />
        </AppNavbar>
        <Body className={styles.views.main(theme)}>
          <InstitutionDashboard institution={institution} institutionSeed={institutionSeed} />
          <Unit className={cx(cssClass.mt('50px'))} />
          <Divider />
          <AppFooter />
          <Unit className={cx(cssClass.mb('50px'))} />
        </Body>
      </LocaleConfigProvider>
    </BuildTimestampProvider>
  );
};

export const getStaticPaths: GetStaticPaths = async (context) => {
  try {
    const serverConfig = configs.server.fromServerEnv(
      await configs.serverEnv.fromEncrypted(services.decrypt.kms)(configs.serverEnv.fromEnv())
    );
    const institutionRecords = await serverConfig.repositories.linkInstitutionRecord.getByLocales();
    const paths = array.bind(
      ([locale, institutionRecord]) =>
        record
          .values(() => 0, institutionRecord)
          .map((institution) => ({
            locale,
            params: models.paramsInstitution.fromInstitution(institution)
          })),
      (context.locales || [])
        .map(models.locale.fromString)
        .map((locale) => [locale, institutionRecords[locale]] as const)
    );

    return {
      paths,
      fallback: false
    };
  } catch (error) {
    return {
      paths: [],
      fallback: false
    };
  }
};

export const getStaticProps: GetStaticProps<Props> = async (context) => {
  try {
    const serverConfig = configs.server.fromServerEnv(
      await configs.serverEnv.fromEncrypted(services.decrypt.kms)(configs.serverEnv.fromEnv())
    );
    const localeConfig = configs.locale.fromStaticRoute({ context, namespace: ['common'] });
    const params = models.paramsInstitution.fromUnknown(context.params || {});
    const translations = await serverSideTranslations(localeConfig.locale, localeConfig.namespace);
    const institutionRecord = await serverConfig.repositories.linkInstitutionRecord.getByLocale(localeConfig.locale);
    const institution = models.institution.fromUnknown(record.lookup(params.localeIdentifier, institutionRecord));
    const institutionSeedRecord = await serverConfig.repositories.institutionSeedRecord.get();
    const institutionSeed = record.lookup(institution.entityKey, institutionSeedRecord);

    return {
      props: {
        ...translations,
        localeConfig,
        institution,
        institutionSeed,
        timestamp: new Date().getTime()
      }
    };
  } catch (error) {
    // TODO: error component?
    throw error;
  }
};

export default Root;
